/*!
 *  (c)(r) 2008-2021 IT-Service Robert Frunzke
 */

import { htmlspecialchars } from './util.js';
import { mj } from './page-mj.js';
import { jQuery } from '../other/jquery-hooked.js';
import { mjt } from './i18ntexts.js';
import { MDTimezones } from './timezone.js';

import { MDLocale } from './locale-impl.js';

export let MDMeta = function($,mj) {

	var m_meta_defs=null;			// object{ meta_id: info, ... }
	var m_meta_grouped=null;		// array( group, group, ... ); group: array( 'id'=>id, 'name'=>name, 'defs'=>array(meta_id,meta_id,...) )
	var m_meta_modified=null;		// timestamp of last change on meta_groups or meta_defs
	var m_qgmd_callbacks=[];

	var m_register_ref_media_meta_field_plugin_fn = null;

	var m_meta_type_names = null;


	function init_meta_type_names() {
		m_meta_type_names = {
			0: mjt('md-def-type-0'),	// String
			1: mjt('md-def-type-1'),	// Text
			2: mjt('md-def-type-2'),	// Integer
			3: mjt('md-def-type-3'),	// Real
			4: mjt('md-def-type-4'),	// Binary
			5: mjt('md-def-type-5'),	// Date
			6: mjt('md-def-type-6'),	// Time
			7: mjt('md-def-type-7'),	// Date+Time
			8: mjt('md-def-type-8'),	// XRef
			9: mjt('md-def-type-9'),	// Object
		};
	}



	function init( result_view_meta_defs, register_std_plugins_fn, register_ref_media_meta_field_plugin_fn ) {
		// store meta-defs, which are required for result view
		// -> full meta defs will be queried when required (by a call to queryGroupedMetaDefs())
		m_meta_defs = result_view_meta_defs || {};
		m_register_ref_media_meta_field_plugin_fn = register_ref_media_meta_field_plugin_fn;

		register_std_plugins_fn();
		_register_plugins_on_known_meta_defs();
	}

	function _register_plugins_on_known_meta_defs() {
		for( var meta_id in m_meta_defs ) {
			if( !m_meta_defs.hasOwnProperty(meta_id) ) continue;
			if( m_meta_defs[meta_id].type==8 ) {
				if( m_register_ref_media_meta_field_plugin_fn ) m_register_ref_media_meta_field_plugin_fn($,meta_id);
			}
		}
	}


	function getAvailableTypes() {
		if( m_meta_type_names===null ) init_meta_type_names();
		return Object.keys(m_meta_type_names);
	}

	function getTypeName( type, is_list, plain_text ) {
		if( m_meta_type_names===null ) init_meta_type_names();
		var str = m_meta_type_names[type];
		if( is_list ) str += ' ' + ( plain_text ? mjt('md-def-type-list-sfx') : '<i>'+mjt('md-def-type-list-sfx')+'</i>' );
		return str;
	}



	function queryGroupedMetaDefs( callback ) {
		m_qgmd_callbacks.push(callback);
		if( m_qgmd_callbacks.length>1 ) {
			// another query is already running..
			// callback will be called when that one finishes
		} else {
			// start new query..
			var postdata = m_meta_modified!==null ? 'if_modified_since='+encodeURIComponent(m_meta_modified) : '';
			$.postJSON('json/media/meta-defs', postdata, function(j) {
				if( !j.not_modified ) {
					m_meta_grouped = j.meta_defs_grouped;
					m_meta_defs = j.meta_defs;
					_register_plugins_on_known_meta_defs();
					if( j.meta_modified ) m_meta_modified = j.meta_modified;
				}
				while( m_qgmd_callbacks.length ) {
					var first_cb = m_qgmd_callbacks.shift();
					first_cb(m_meta_grouped,m_meta_defs);
				}
			});
		}
	}

	function queryDefinition( meta_id, callback ) {
		meta_id = meta_id.toString();
		if( m_meta_defs.hasOwnProperty(meta_id) ) {
			callback(m_meta_defs[meta_id]);
		} else if( m_meta_grouped===null ) {
			queryGroupedMetaDefs( function() {
				callback( getDefinition(meta_id) );
			} );
		} else {
			callback({});
		}
	}

	function getDefinition( meta_id ) {
		meta_id = meta_id.toString();
		return m_meta_defs.hasOwnProperty(meta_id) ? m_meta_defs[meta_id] : {};
	}

	function getDefinitionByName( name ) {
		for( var meta_id in m_meta_defs ) {
			if( !m_meta_defs.hasOwnProperty(meta_id) ) continue;
			if( m_meta_defs[meta_id].name==name ) return m_meta_defs[meta_id];
		}
		return null;
	}

	function getType( meta_id ) {
		meta_id = meta_id.toString();
		return m_meta_defs.hasOwnProperty(meta_id) ? m_meta_defs[meta_id].type : 0;
	}

	function getIsList( meta_id ) {
		meta_id = meta_id.toString();
		return m_meta_defs.hasOwnProperty(meta_id) ? !!m_meta_defs[meta_id].list : false;
	}

	function getNumberFormat( meta_id ) {
		meta_id = meta_id.toString();
		return m_meta_defs.hasOwnProperty(meta_id) && m_meta_defs[meta_id].options.num_fmt ? m_meta_defs[meta_id].options.num_fmt : 'normal';
	}

	function MDMetaRenderer( meta_id ) {
		this.meta_id = meta_id;
		this.md = getDefinition(meta_id);
		// this.has_complex_html:
		//  false = data can be be queried once and used in plain text as well as html (after conversion through htmlspecialchars())
		//  true = data should be queried for plain text and html separately
		this.has_complex_html = false;
		switch( this.md.type ) {
		case 2: // MT_INT
		case 8: // MT_REF_MEDIA
			this.has_complex_html = true;
			this._get_data_item = function(val,plain_text) {
				val = parseInt(val,10);
				if( isNaN(val) ) return '';
				if( meta_id==-1 ) return plain_text ? mj.mc_names[val] : htmlspecialchars(mj.mc_names[val]); // convert media class id to string
				return MDLocale.fmt_number( val, getNumberFormat(meta_id), plain_text );
			};
			break;
		case 3: // MT_REAL
			this.has_complex_html = true;
			this._get_data_item = function(val,plain_text) {
				val = parseFloat(val);
				if( isNaN(val) ) return '';
				return MDLocale.fmt_number( val, getNumberFormat(meta_id), plain_text );
			};
			break;
		case 4: // MT_BINARY
			this._get_data_item = function(val,plain_text) {
				return '-binary-data-';
			};
			break;
		case 5: // MT_DATE
			this._get_data_item = function(val,plain_text) {
				return MDLocale.fmt_date(val);
			};
			break;
		case 6: // MT_TIME
			this._get_data_item = function(val,plain_text) {
				return MDLocale.fmt_time(val);
			};
			break;
		case 7: // MT_DATETIME
			this._get_data_item = function(val,plain_text) {
				if( val.length==10 ) return MDLocale.fmt_date(val);				// date only
				return MDLocale.fmt_datetime( MDTimezones.datetime_utc2local(val) );	// date+time
			};
			break;
		case 9: // MT_OBJECT
			var object_sub_type = this.md.hasOwnProperty('options') && this.md.options.hasOwnProperty('object_sub_type') ? this.md.options.object_sub_type : '';
			if( object_sub_type==='file-list' ) {
				this.has_complex_html = true;
				this._get_data_item = function(val,plain_text) {
					var fe = JSON.parse(val);
					// keys: name, size, mtime
					var size_str = fe.size<0 ? '' : MDLocale.fmt_number(fe.size,'filesize',plain_text);
					var mtime_local = MDTimezones.datetime_any2local(fe.mtime);
					var mtime_str = MDLocale.fmt_datetime_detailed(mtime_local);
					if( plain_text ) {
						var str = fe.name||'';
						str += ' ('+size_str+')';
						str += ' '+mtime_str;
						return str;
					}
					var fileType = fe.name.endsWith('/') ? 'folder' : 'file';
					var html = '<span class="file-list-row '+fileType+'">';
					html += '<span class="fl-name">'+htmlspecialchars(fe.name||'')+'</span>';
					if( fileType=='file' ) html += '<span class="fl-size">'+size_str+'</span>';
					html += '<span class="fl-mtime">'+mtime_str.replace(/, /,',<wbr> ')+'</span>';
					html += '</span>';
					return html;
				};
			} else if( object_sub_type==='voting-list' ) {
				this.has_complex_html = true;
				this._get_data_item = function(val,plain_text) {
					var fe = JSON.parse(val);
					// keys: voting_id, title, created_dt, closed_dt
					// var created_dt_str = MDLocale.fmt_datetime_detailed(MDTimezones.datetime_utc2local(fe.created_dt));
					var closed_dt_local = MDTimezones.datetime_any2local(fe.closed_dt);
					var closed_dt_str = MDLocale.fmt_datetime_detailed(closed_dt_local);
					if( plain_text ) {
						var str = fe.title||'';
						str += ' ('+fe.voting_id+')';
						//str += ' '+created_dt_str;
						str += ' '+closed_dt_str;
						return str;
					}
					var approved_text = fe.approved ? mjt('meta-def-collaboration-voting-list-finally-approved') : mjt('meta-def-collaboration-voting-list-finally-rejected');
					var html = '<span class="voting-list-row">';
					html += '<span class="vl-title">'+htmlspecialchars(fe.title||'')+'</span>';
					// html += '<span class="vl-created">'+created_dt_str.replace(/, /,',<wbr> ')+'</span>';
					html += '<span class="vl-closed">'+closed_dt_str.replace(/, /,',<wbr> ')+'</span>';
					html += '<span class="vl-approved vl-approved-'+(fe.approved?'true':'false')+'">'+approved_text+'</span>';
					html += '</span>';
					return html;
				};
			} else {
				this._get_data_item = function(val,plain_text) {
					return plain_text ? val : htmlspecialchars(val);
				};
			}
			break;
		case 0: // MT_STRING
		case 1: // MT_TEXT
		default:
			this._get_data_item = function(val,plain_text) {
				return plain_text ? val : htmlspecialchars(val);
			};
		}
		// this.vl_t - value lists translations
		this.vl_t = null;
		if( this.md.hasOwnProperty('options') && this.md.options.hasOwnProperty('vl_mode') && this.md.options.vl_mode==='fixed' && this.md.options.hasOwnProperty('vl_translations') && this.md.options.vl_translations!=='' ) {
			this.vl_t = JSON.parse(this.md.options.vl_translations);
		}
	}
	MDMetaRenderer.prototype = {
		hasComplexHTMLDisplay: function() {
			return this.has_complex_html;
		},
		getDisplayDataItem: function( val, plain_text ) {
			if( this.vl_t!==null && this.vl_t.hasOwnProperty(val) && this.vl_t[val].hasOwnProperty(mj.locale) ) val = this.vl_t[val][mj.locale];
			return this._get_data_item(val,plain_text);
		},
		getDisplayData: function( val_arr, plain_text ) {
			var vl=val_arr.length, i, data=[];
			for( i=0; i<vl; ++i ) data.push(this.getDisplayDataItem(val_arr[i],plain_text));
			return data.join(', ');
		},
	};

	function getDisplayDataRenderer( meta_id ) {
		return new MDMetaRenderer(meta_id);
	}

	function getDisplayDataItem( val, meta_id, plain_text ) {
		var r = getDisplayDataRenderer(meta_id);
		return r.getDisplayDataItem(val,plain_text);
	}

	function getDisplayData( val_arr, meta_id, plain_text ) {
		var r = getDisplayDataRenderer(meta_id);
		return r.getDisplayData(val_arr,plain_text);
	}

	return {
		init: init,

		getAvailableTypes: getAvailableTypes,
		getTypeName: getTypeName,

		queryGroupedMetaDefs: queryGroupedMetaDefs,

		queryDefinition: queryDefinition,
		getDefinition: getDefinition,
		getDefinitionByName: getDefinitionByName,
		getType: getType,
		getIsList: getIsList,
		getNumberFormat: getNumberFormat,

		getDisplayDataRenderer: getDisplayDataRenderer,

		getDisplayDataItem: getDisplayDataItem,
		getDisplayData: getDisplayData,
	};
}(jQuery,mj); // namespace MDMeta
